const DEFAULT_EXCLUDED_ROUTES = ["/login", "/SelectProject"];

export const normalizePath = (value) => {
  if (!value) return "";

  const map = {
    ı: "i",
    İ: "i",
    ş: "s",
    Ş: "s",
    ğ: "g",
    Ğ: "g",
    ü: "u",
    Ü: "u",
    ö: "o",
    Ö: "o",
    ç: "c",
    Ç: "c",
  };

  return value
    .toString()
    .trim()
    .split("")
    .map((char) => map[char] || char)
    .join("")
    .toLowerCase()
    .replace(/^\/+|\/+$/g, "")
    .replace(/\s+/g, "-");
};

export const flattenMenuPages = (menuPages) => {
  if (!Array.isArray(menuPages) || menuPages.length === 0) {
    return [];
  }

  const result = [];
  const collect = (pages) => {
    if (!Array.isArray(pages)) return;
    pages.forEach((page) => {
      if (!page) return;
      result.push(page);
      if (Array.isArray(page.children) && page.children.length > 0) {
        collect(page.children);
      }
    });
  };

  collect(menuPages);
  return result;
};

const isExcludedRoute = (routePath, excludedRoutes) => {
  return excludedRoutes.some((path) => path === routePath);
};

const getStaticRoutePath = (routePath) => {
  if (!routePath) return "";
  return routePath.split("/:")[0];
};

const getMenuCandidates = (menuValue, routeAliases = {}) => {
  const normalizedMenuUrl = normalizePath(menuValue);
  if (!normalizedMenuUrl) return [];

  const candidates = [normalizedMenuUrl];
  const alias = routeAliases[normalizedMenuUrl];
  if (alias) {
    candidates.push(alias);
  }

  return candidates;
};

const isPathMatching = (normalizedRoutePath, candidate) => {
  if (!normalizedRoutePath || !candidate) return false;
  return (
    normalizedRoutePath === candidate ||
    normalizedRoutePath.startsWith(`${candidate}/`) ||
    candidate.startsWith(`${normalizedRoutePath}/`)
  );
};

export const resolveRouteFromMenuValue = (
  menuValue,
  allRoutes,
  options = {}
) => {
  const excludedRoutes = options.excludedRoutes || DEFAULT_EXCLUDED_ROUTES;
  const routeAliases = options.routeAliases || {};
  const menuCandidates = getMenuCandidates(menuValue, routeAliases);
  if (menuCandidates.length === 0) return null;

  for (const route of allRoutes) {
    if (!route || !route.path || isExcludedRoute(route.path, excludedRoutes)) {
      continue;
    }

    const normalizedRoutePath = normalizePath(getStaticRoutePath(route.path));
    if (!normalizedRoutePath) continue;

    const hasMatch = menuCandidates.some((candidate) =>
      isPathMatching(normalizedRoutePath, candidate)
    );

    if (hasMatch) {
      return route.path;
    }
  }

  return null;
};

export const resolveRoutesFromMenuValue = (
  menuValue,
  allRoutes,
  options = {}
) => {
  const excludedRoutes = options.excludedRoutes || DEFAULT_EXCLUDED_ROUTES;
  const routeAliases = options.routeAliases || {};
  const menuCandidates = getMenuCandidates(menuValue, routeAliases);
  if (menuCandidates.length === 0) return [];

  return allRoutes.filter((route) => {
    if (!route || !route.path || isExcludedRoute(route.path, excludedRoutes)) {
      return false;
    }

    const normalizedRoutePath = normalizePath(getStaticRoutePath(route.path));
    if (!normalizedRoutePath) return false;

    return menuCandidates.some((candidate) =>
      isPathMatching(normalizedRoutePath, candidate)
    );
  });
};

const getMenuSegments = (allPages) => {
  const segments = new Set();

  allPages.forEach((page) => {
    const normalized = normalizePath((page && page.url) || (page && page.path));
    if (!normalized) return;
    const [segment] = normalized.split("/");
    if (segment) {
      segments.add(segment);
    }
  });

  return segments;
};

export const resolveFirstAuthorizedRoutePath = (
  menuPages,
  allRoutes,
  options = {}
) => {
  const allPages = flattenMenuPages(menuPages);
  if (allPages.length === 0) return null;

  const excludedRoutes = options.excludedRoutes || DEFAULT_EXCLUDED_ROUTES;

  for (const page of allPages) {
    const match = resolveRouteFromMenuValue(
      page.url || page.path,
      allRoutes,
      options
    );
    if (match) {
      return match;
    }
  }

  const menuSegments = getMenuSegments(allPages);
  if (menuSegments.size === 0) return null;

  for (const route of allRoutes) {
    if (!route || !route.path || isExcludedRoute(route.path, excludedRoutes)) {
      continue;
    }

    const routeSegment = normalizePath(getStaticRoutePath(route.path)).split("/")[0];
    if (routeSegment && menuSegments.has(routeSegment)) {
      return route.path;
    }
  }

  return null;
};

export const getAuthorizedRoutesFromMenu = (menuPages, allRoutes, options = {}) => {
  const allPages = flattenMenuPages(menuPages);
  if (allPages.length === 0) return [];

  const authorizedPaths = new Set();
  allPages.forEach((page) => {
    const matches = resolveRoutesFromMenuValue(
      page.url || page.path,
      allRoutes,
      options
    );
    matches.forEach((route) => {
      authorizedPaths.add(route.path);
    });
  });

  return allRoutes.filter((route) => route && authorizedPaths.has(route.path));
};

export const normalizeMenuHierarchy = (menuItems) => {
  if (!Array.isArray(menuItems)) return [];

  const menuById = new Map();
  const topLevelItems = [];
  const flatMenuItems = flattenMenuPages(menuItems);

  flatMenuItems.forEach((item) => {
    menuById.set(item._id, { ...item, children: [] });
  });

  menuById.forEach((item) => {
    if (item.parentId && menuById.has(item.parentId)) {
      const parent = menuById.get(item.parentId);
      const childExists = parent.children.some((child) => child._id === item._id);
      if (!childExists) {
        parent.children.push(item);
      }
      return;
    }
    topLevelItems.push(item);
  });

  const cleanEmptyChildren = (items) => {
    items.forEach((item) => {
      if (item.children && item.children.length === 0) {
        delete item.children;
      } else if (item.children && item.children.length > 0) {
        cleanEmptyChildren(item.children);
      }
    });
  };

  cleanEmptyChildren(topLevelItems);
  return topLevelItems;
};
